import { LayoutHeader } from "@/Header";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useState } from "react";
import { useContractInbox } from "~/services/customer/contractRequest";
import type { CdRequestStatus } from "~/types/common";

/**
 * 보호자가 사업장으로부터 받은 서명 요청 목록
 * 알림장/푸시에서 진입하거나 마이페이지 → 서명 요청함 으로 진입
 */

export const Route = createFileRoute("/customer/layout/contract-inbox")({
  component: RouteComponent,
});

type Filter = "all" | "pending" | "completed" | "rejected";

const FILTER_STATUS: Record<Exclude<Filter, "all">, CdRequestStatus[]> = {
  pending: ["REQUEST_STATUS_SENT", "REQUEST_STATUS_OPENED"],
  completed: ["REQUEST_STATUS_SIGNED", "REQUEST_STATUS_COMPLETED"],
  rejected: ["REQUEST_STATUS_REJECTED", "REQUEST_STATUS_EXPIRED", "REQUEST_STATUS_CANCELED"],
};

function RouteComponent() {
  const { history } = useRouter();
  const [filter, setFilter] = useState<Filter>("all");

  const { data: rows = [], isLoading } = useContractInbox();

  const filtered = rows.filter((r) => {
    if (filter === "all") return true;
    return FILTER_STATUS[filter].includes(r.cdRequestStatus);
  });

  const counts = {
    all: rows.length,
    pending: rows.filter((r) => FILTER_STATUS.pending.includes(r.cdRequestStatus)).length,
    completed: rows.filter((r) => FILTER_STATUS.completed.includes(r.cdRequestStatus))
      .length,
    rejected: rows.filter((r) => FILTER_STATUS.rejected.includes(r.cdRequestStatus))
      .length,
  };

  return (
    <>
      <LayoutHeader title="서명 요청함" hasBackBtn hasFootPrint={false} />
      <div className="flex-auto bg-[#F5F5F5] pb-8">
        {/* 필터 */}
        <div className="flex gap-2 px-4 py-3 overflow-x-auto">
          <Pill active={filter === "all"} onClick={() => setFilter("all")}>
            전체 ({counts.all})
          </Pill>
          <Pill
            active={filter === "pending"}
            onClick={() => setFilter("pending")}
          >
            서명 대기 ({counts.pending})
          </Pill>
          <Pill
            active={filter === "completed"}
            onClick={() => setFilter("completed")}
          >
            완료 ({counts.completed})
          </Pill>
          <Pill
            active={filter === "rejected"}
            onClick={() => setFilter("rejected")}
          >
            거절·만료 ({counts.rejected})
          </Pill>
        </div>

        {/* 목록 */}
        <div className="mx-4 space-y-2">
          {isLoading && (
            <div className="text-center text-[#999] text-[13px] py-12">불러오는 중...</div>
          )}
          {!isLoading && filtered.length === 0 && (
            <div className="text-center text-[#999] text-[13px] py-12">
              해당 항목이 없어요
            </div>
          )}
          {filtered.map((r) => {
            const isPending =
              r.cdRequestStatus === "REQUEST_STATUS_SENT" ||
              r.cdRequestStatus === "REQUEST_STATUS_OPENED";
            const isCompleted =
              r.cdRequestStatus === "REQUEST_STATUS_COMPLETED" ||
              r.cdRequestStatus === "REQUEST_STATUS_SIGNED";
            return (
              <div
                key={r.requestSeq}
                onClick={() => {
                  if (isPending) {
                    history.push(
                      `/customer/layout/contract-sign?requestSeq=${r.requestSeq}` as any
                    );
                  } else if (isCompleted) {
                    history.push(
                      `/customer/layout/contract-sign?requestSeq=${r.requestSeq}` as any
                    );
                  } else {
                    alert("상세 보기는 준비 중이에요");
                  }
                }}
                className="bg-white rounded-xl border border-[#E9E9E9] p-3 cursor-pointer active:bg-[#FAFAFA]"
              >
                <div className="flex items-center gap-2 mb-1.5">
                  <StatusBadge status={r.cdRequestStatus} />
                  <div className="text-[14px] font-semibold text-[#111] flex-1">
                    {r.companyName ?? "—"}
                  </div>
                  <span className="text-[10px] text-[#999]">
                    {r.dtSent?.slice(0, 10)}
                  </span>
                </div>
                <div className="text-[12px] text-[#666] mb-1">
                  {r.contractName}
                  {r.petName ? ` · ${r.petName}` : ""}
                </div>
                {isPending && r.dtExpired && (
                  <div className="text-[10px] text-[#F24747] mt-1.5">
                    ⏰ {r.dtExpired.replace("T", " ").slice(0, 16)} 까지
                  </div>
                )}
                {isCompleted && (
                  <div className="text-[11px] text-[#22A55B] font-semibold mt-1.5">
                    완료된 계약서 보기 →
                  </div>
                )}
                {r.rejectReason && (
                  <div className="text-[11px] text-[#F24747] bg-[#FFF1F1] rounded p-2 mt-1.5">
                    💬 {r.rejectReason}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}

const Pill = ({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={`shrink-0 text-[12px] font-semibold rounded-full px-3 py-1.5 ${
      active
        ? "bg-[#FF9934] text-white"
        : "bg-white text-[#666] border border-[#E9E9E9]"
    }`}
  >
    {children}
  </button>
);

const STATUS_MAP: Record<CdRequestStatus, { label: string; bg: string; color: string }> = {
  REQUEST_STATUS_SENT: { label: "서명 대기", bg: "bg-[#FFF7E6]", color: "text-[#FF9934]" },
  REQUEST_STATUS_OPENED: { label: "열람함", bg: "bg-[#F5F3FF]", color: "text-[#7D6DFF]" },
  REQUEST_STATUS_SIGNED: { label: "서명함", bg: "bg-[#F0FDF4]", color: "text-[#22A55B]" },
  REQUEST_STATUS_COMPLETED: { label: "완료", bg: "bg-[#F0FDF4]", color: "text-[#22A55B]" },
  REQUEST_STATUS_REJECTED: { label: "거절", bg: "bg-[#FFF1F1]", color: "text-[#F24747]" },
  REQUEST_STATUS_EXPIRED: { label: "만료", bg: "bg-[#F5F5F5]", color: "text-[#999]" },
  REQUEST_STATUS_CANCELED: { label: "취소", bg: "bg-[#F5F5F5]", color: "text-[#999]" },
};

const StatusBadge = ({ status }: { status: CdRequestStatus }) => {
  const { label, bg, color } = STATUS_MAP[status] ?? STATUS_MAP.REQUEST_STATUS_SENT;
  return (
    <span
      className={`inline-block text-[10px] font-bold rounded px-1.5 py-0.5 ${bg} ${color}`}
    >
      {label}
    </span>
  );
};
